import styled from 'styled-components';
import { FormikErrors, FormikTouched, FormikValues } from 'formik';

import { APPAREL_TYPE_MAP } from 'productTemplate/enums/apparelTypesMap';
import { FitFeedbackFormActions } from 'productTemplate/state/fitFeedbackFormReducer';
import { getFitFeedbackApparelCategoryType } from 'productTemplate/utils/getFitFeedbackApparelCategoryType';
import { QuestionForm } from 'shared/components/QuestionForm';
import { transformSizeOptions } from 'productTemplate/utils/transformSizeOptionsForFitFeedbackForm';
import { useStoreSelector } from 'store/hooks/storeHooks';
import {
  useFitFeedbackFormDispatch,
  useFitFeedbackFormState,
} from 'productTemplate/providers/FitFeedbackProvider';

import { variableFont, variableFontMedium } from 'styles/theme';

interface IFitFeedbackFormApparel {
  setFieldValue: (key: string, val: string) => void;
  errors: FormikErrors<{ [key: string]: string }>;
  touched: FormikTouched<FormikValues>;
}

const letterSizeGridStyle = {
  display: 'grid',
  gridGap: '16px',
  gridTemplateColumns: 'repeat(2, 1fr)',
};

export const FitFeedbackFormApparel = ({
  setFieldValue,
  errors,
  touched,
}: IFitFeedbackFormApparel) => {
  const {
    sizeOptions,
    productType,
    name: productName,
  } = useStoreSelector((state) => state.pdp.productTemplate);
  const transformedSizeOptions = transformSizeOptions(sizeOptions);
  const dispatch = useFitFeedbackFormDispatch();
  const { style, sizePurchased, sizeFit } = useFitFeedbackFormState();
  const styleError = touched?.style && errors?.style;
  const sizePurchasedError = touched?.sizePurchased && errors?.sizePurchased;
  const sizeFitError = touched?.sizeFit && errors?.sizeFit;
  const isLetterSize = Number.isNaN(Number(sizeOptions?.[0]?.presentation));
  const productCategoryType = getFitFeedbackApparelCategoryType(productType, productName);

  const apparelType = APPAREL_TYPE_MAP?.[productCategoryType];

  if (!apparelType) {
    return null;
  }

  const handleOnChange = (type) => (e) => {
    const { value, name } = e.target;
    dispatch({ type, payload: value });
    setFieldValue(name, value);
  };

  return (
    <FitFeedbackFormApparel.Wrapper>
      <QuestionForm
        checked={style}
        data-qa="fit_feedback"
        error={styleError}
        gridStyle={{ display: 'grid', gridGap: '16px', gridTemplateColumns: 'repeat(3, 1fr)' }}
        name="style"
        onChange={handleOnChange(FitFeedbackFormActions.setStyle)}
        options={apparelType?.options}
        question={apparelType?.questions?.[0]}
        choiceClassName="sizeGrid"
        className="questionText"
        withBorders
      />
      <FitFeedbackFormApparel.QuestionForm
        checked={sizePurchased}
        data-qa="fit_feedback"
        error={sizePurchasedError}
        gridStyle={isLetterSize ? letterSizeGridStyle : null}
        name="sizePurchased"
        choiceClassName="sizeGrid"
        onChange={handleOnChange(FitFeedbackFormActions.setSizePurchased)}
        options={transformedSizeOptions}
        question={apparelType.questions?.[1]}
        className="questionText"
        withBorders
      />
      <FitFeedbackFormApparel.QuestionForm
        checked={sizeFit}
        data-qa="fit_feedback"
        error={sizeFitError}
        gridStyle={isLetterSize ? letterSizeGridStyle : null}
        name="sizeFit"
        choiceClassName="sizeGrid"
        onChange={handleOnChange(FitFeedbackFormActions.setSizeFit)}
        options={transformedSizeOptions}
        question={apparelType?.questions?.[2]}
        className="questionText"
        withBorders
      />
    </FitFeedbackFormApparel.Wrapper>
  );
};

FitFeedbackFormApparel.Wrapper = styled.div`
  margin: 16px 0 -40px 0;

  .questionText {
    legend {
      @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
        ${variableFontMedium({ size: '12.5px' })}
      }
    }
  }

  .checkboxGrid {
    ${variableFont({ size: '12px', height: '12px', fontWeight: '400' })};

    @media (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
      ${variableFont({ size: '12.5px' })}
    }
  }

  .sizeGrid {
    min-height: 40px;
    min-width: 40px;
    ${variableFont({ height: '12px', size: '12px', fontWeight: '400' })}
    border-radius: ${({ theme }) => theme.borderRadii.button};

    @media (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
      ${variableFont({ size: '12.5px', fontWeight: '400' })}
    }
  }
`;

FitFeedbackFormApparel.QuestionForm = styled(QuestionForm)`
  label {
    text-transform: uppercase;
  }
`;
